/* ----------------------------------------------------------------------------
 * insightText — pure parser behind <InsightText>. No React, no JSX, no imports,
 * so it is unit-testable directly (node:test) and shared by one component only.
 *
 * Model / advisory bodies (SEO insights, the executive summary) can arrive with
 * light Markdown (`# heading`, `**bold**`, `-`/`1.` lists, `` `code` ``). These
 * helpers turn that into clean paragraph/list blocks: supported inline emphasis
 * becomes real spans, every raw marker is removed, and all figures (currency,
 * %, plain numbers) pass through verbatim. Meaning is never altered — only the
 * syntax is cleaned.
 * -------------------------------------------------------------------------- */

export type InlineSpan =
  | { kind: 'text'; value: string }
  | { kind: 'strong'; value: string }
  | { kind: 'em'; value: string }
  | { kind: 'code'; value: string };

export type InsightBlock =
  | { kind: 'p'; spans: InlineSpan[] }
  | { kind: 'ul'; items: InlineSpan[][] }
  | { kind: 'ol'; items: InlineSpan[][] };

const UNORDERED_RE = /^\s*[-*+]\s+(.*)$/;
const ORDERED_RE = /^\s*\d+[.)]\s+(.*)$/;
const HEADING_RE = /^\s*#{1,6}\s+/;
const BLOCKQUOTE_RE = /^\s*>\s?/;

/** Remove residual block markers (heading hashes, blockquote chevrons) from a
 *  line, so a stripped heading reads as ordinary prose. */
function stripBlockMarkers(line: string): string {
  return line.replace(HEADING_RE, '').replace(BLOCKQUOTE_RE, '');
}

/** Final defensive sweep on a plain-text run: drop leftover emphasis fences an
 *  unclosed marker could leave visible. Lone `*`/`_` inside prose (e.g. "5 * 3",
 *  "size 8_9") are preserved — only clear markup (`**`, `__`, backticks) and
 *  stray leading hashes are removed. */
function cleanResidual(value: string): string {
  return value
    .replace(/\*\*/g, '')
    .replace(/__/g, '')
    .replace(/`/g, '')
    .replace(/(^|\s)#{1,6}(\s)/g, '$1$2');
}

/** Strip emphasis markers from a run that already sits inside a strong/em/code
 *  span — the wrapper carries the emphasis, so nested fences are noise. */
function stripInnerMarkers(value: string): string {
  return value.replace(/\*\*|__|`/g, '').replace(/(^|\s)#{1,6}(\s)/g, '$1$2');
}

/**
 * Parse one line into inline spans, converting well-formed `` `code` ``,
 * `**strong**` / `__strong__`, and `*em*` / `_em_` pairs into elements. Only
 * properly-closed, non-empty, non-space-padded pairs convert; anything else is
 * literal text, then swept by cleanResidual so no raw marker survives.
 */
export function parseInline(input: string): InlineSpan[] {
  const spans: InlineSpan[] = [];
  let buf = '';
  let i = 0;

  const flush = () => {
    if (buf) {
      const cleaned = cleanResidual(buf);
      if (cleaned) spans.push({ kind: 'text', value: cleaned });
      buf = '';
    }
  };

  while (i < input.length) {
    if (input[i] === '`') {
      const close = input.indexOf('`', i + 1);
      if (close > i + 1) {
        flush();
        spans.push({ kind: 'code', value: input.slice(i + 1, close) });
        i = close + 1;
        continue;
      }
    }
    if (input.startsWith('**', i) || input.startsWith('__', i)) {
      const marker = input.slice(i, i + 2);
      const close = input.indexOf(marker, i + 2);
      if (close > i + 2 && input[i + 2] !== ' ' && input[close - 1] !== ' ') {
        flush();
        spans.push({ kind: 'strong', value: stripInnerMarkers(input.slice(i + 2, close)) });
        i = close + 2;
        continue;
      }
    }
    if (input[i] === '*' || input[i] === '_') {
      const marker = input[i];
      const close = input.indexOf(marker, i + 1);
      if (
        close > i + 1 &&
        input[i + 1] !== ' ' &&
        input[close - 1] !== ' ' &&
        input[close + 1] !== marker &&
        input[i - 1] !== marker
      ) {
        flush();
        spans.push({ kind: 'em', value: stripInnerMarkers(input.slice(i + 1, close)) });
        i = close + 1;
        continue;
      }
    }
    buf += input[i];
    i += 1;
  }
  flush();
  return spans.length ? spans : [{ kind: 'text', value: '' }];
}

/**
 * Parse a block of text into clean paragraph and list blocks. Blank lines break
 * paragraphs; runs of `-`/`*`/`+` items become a `ul`; runs of `1.`/`1)` items
 * become an `ol`; heading/blockquote markers are stripped to prose. Zero
 * visible Markdown; wording kept verbatim.
 */
export function parseInsightText(raw: string): InsightBlock[] {
  const text = (raw ?? '').replace(/\r\n?/g, '\n').replace(/```+/g, '').trim();
  if (!text) return [];

  const lines = text.split('\n');
  const blocks: InsightBlock[] = [];
  let paragraph: string[] = [];
  let list: { kind: 'ul' | 'ol'; items: InlineSpan[][] } | null = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      const joined = paragraph.join(' ').trim();
      if (joined) blocks.push({ kind: 'p', spans: parseInline(joined) });
      paragraph = [];
    }
  };
  const flushList = () => {
    if (list && list.items.length) blocks.push(list);
    list = null;
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }
    const unordered = line.match(UNORDERED_RE);
    const ordered = line.match(ORDERED_RE);
    if (unordered || ordered) {
      flushParagraph();
      const wantKind: 'ul' | 'ol' = ordered ? 'ol' : 'ul';
      if (!list || list.kind !== wantKind) {
        flushList();
        list = { kind: wantKind, items: [] };
      }
      const itemText = (ordered ? ordered[1] : unordered![1]).trim();
      list.items.push(parseInline(itemText));
      continue;
    }
    flushList();
    paragraph.push(stripBlockMarkers(line));
  }
  flushParagraph();
  flushList();
  return blocks;
}

/** Flatten model/insight text to one clean plain-text string (no markers, no
 *  line breaks) — for inline contexts (table cells) where block layout would
 *  not fit. Figures and wording preserved verbatim. */
export function stripInsightMarkdown(raw: string): string {
  const spansText = (spans: InlineSpan[]) => spans.map((s) => s.value).join('');
  return parseInsightText(raw)
    .map((b) => (b.kind === 'p' ? spansText(b.spans) : b.items.map(spansText).join('; ')))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}
